import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button.tsx';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.tsx';
import { cn, formatValueOrDefault } from '@/utils/utils.ts';
import {
  TBMSDeviceModuleStatus,
  useGetBMSStatusDeviceQuery,
} from '@/features/devices/hooks/queries/[device]/get-bms-status-device.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useTranslation } from 'react-i18next';

type TranslationKeys =
  | 'devices.select.parameters.voltage'
  | 'devices.select.parameters.temperature'
  | 'devices.select.parameters.stateOfHealth';

export function TemporaryReplaceModuleSection({ deviceId }: { deviceId: string | undefined }) {
  const { t } = useTranslation();

  const { data } = useGetBMSStatusDeviceQuery({
    deviceId,
    moduleNumber: undefined,
  });

  return (
    <div>
      <div className="h-[42px]"></div>
      <div>
        <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <li className="grid grid-rows-2">
            <span className="text-muted-foreground text-sm text-nowrap">
              {t('devices.select.parameters.minimumVoltageCell')}
            </span>
            <span className="font-medium">
              {formatValueOrDefault(data?.batteryMinCellVoltage, 'V')}{' '}
            </span>
          </li>
          <li className="grid grid-rows-2">
            <span className="text-muted-foreground text-sm text-nowrap">
              {t('devices.select.parameters.maximumVoltageCell')}
            </span>
            <span className="font-medium">
              {formatValueOrDefault(data?.batteryMaxCellVoltage, 'V')}{' '}
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export function BatteryModuleSection({ deviceId }: { deviceId: string | undefined }) {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState(t('devices.batteryModule.module1'));
  const moduleNumber = Number(selectedValue.split(' ')[1])!;

  const { data: deviceModuleStatus } = useGetBMSStatusDeviceQuery({
    deviceId,
    moduleNumber,
  });

  const { data: deviceStatus } = useGetBMSStatusDeviceQuery({
    deviceId,
    moduleNumber: undefined,
  });

  const availableModules = useMemo(
    () =>
      Array.from({ length: deviceStatus?.availableCellModules || 12 }, (_, i) => ({
        label: `${t('devices.batteryModule.module')}${i + 1}`,
        value: String(i + 1),
      })),
    [deviceStatus?.availableCellModules, t]
  );

  return (
    <div className="flex flex-col gap-3">
      <ModuleSelector
        selectedValue={selectedValue}
        availableModules={availableModules}
        onSelect={setSelectedValue}
      />
      <div className="flex h-full flex-col gap-4">
        <ModuleStatusSection data={deviceModuleStatus} />
        <CellManagement deviceId={deviceId} moduleNumber={moduleNumber} />
      </div>
    </div>
  );
}

type CellParameters = 'voltage' | 'temperature' | 'soh';

const PARAMETER_COLORS: Record<CellParameters, string> = {
  voltage: '#7ad1dc',
  temperature: '#fb7185',
  soh: '#60a5fa',
};

const PARAMETER_LABELS: Record<CellParameters, TranslationKeys> = {
  voltage: 'devices.select.parameters.voltage',
  temperature: 'devices.select.parameters.temperature',
  soh: 'devices.select.parameters.stateOfHealth',
} as const;

const PARAMETER_UNITS: Record<CellParameters, string> = {
  voltage: 'V',
  temperature: '°C',
  soh: '%',
};

function ModuleSelector({
  selectedValue,
  availableModules,
  onSelect,
}: {
  selectedValue: string;
  availableModules: {
    label: string;
    value: string;
  }[];
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-[130px] justify-between px-3 font-normal"
        >
          {selectedValue || t('devices.module.selectModule')}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="start">
        <Command>
          <CommandInput placeholder={t('devices.module.searchModule')} />
          <CommandList>
            <CommandEmpty>{t('devices.errors.noModule')}</CommandEmpty>
            <CommandGroup>
              {availableModules.map(({ value, label }) => (
                <CommandItem
                  key={value}
                  value={label}
                  onSelect={(currentValue) => {
                    onSelect(currentValue === selectedValue ? selectedValue : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      label === selectedValue ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function StatusItem({
  label,
  value,
  unit,
}: {
  label: string;
  value: number | undefined | null;
  unit: string;
}) {
  return (
    <li className="grid grid-rows-2">
      <span className="text-muted-foreground text-sm text-nowrap">{label}</span>
      <span className="line-clamp-2 font-medium text-ellipsis">
        {formatValueOrDefault(value, unit)}
      </span>
    </li>
  );
}

const ModuleStatusSection = function ({ data }: { data: TBMSDeviceModuleStatus | undefined }) {
  const { t } = useTranslation();
  return (
    <>
      <div>
        <ul className="grid grid-cols-2 sm:grid-cols-3">
          <StatusItem
            label={t('devices.select.parameters.stateOfCharge')}
            value={data?.stateOfCharge}
            unit="%"
          />
          <StatusItem
            label={t('devices.select.parameters.moduleVoltage')}
            value={data?.moduleVoltage}
            unit="V"
          />
          <StatusItem
            label={t('devices.select.parameters.stringCurrent')}
            value={data?.stringCurrent}
            unit="A"
          />
        </ul>
      </div>
      <div>
        <ul>
          <StatusItem
            label={t('devices.select.parameters.temperatureABO')}
            value={data?.temperatureABO}
            unit="°C"
          />
        </ul>
      </div>
      <div>
        <ul className="grid grid-cols-2 gap-4">
          <StatusItem
            label={t('devices.select.parameters.minimumVoltageCell')}
            value={data?.minimumVoltageCell}
            unit="V"
          />
          <StatusItem
            label={t('devices.select.parameters.maximumVoltageCell')}
            value={data?.maximumVoltageCell}
            unit="V"
          />
          <StatusItem
            label={t('devices.select.parameters.minimumStateOfHealth')}
            value={data?.minimumStateOfHealth}
            unit="%"
          />
          <StatusItem
            label={t('devices.select.parameters.maximumStateOfHealth')}
            value={data?.maximumStateOfHealth}
            unit="%"
          />
        </ul>
      </div>
    </>
  );
};

function ParameterSelect({
  value,
  onChange,
  disabled,
}: {
  value: CellParameters;
  onChange: (value: CellParameters) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const translatedLabels = useMemo(
    () =>
      Object.entries(PARAMETER_LABELS).reduce<Record<CellParameters, string>>(
        (acc, [key, translationKey]) => ({
          ...acc,
          [key]: t(translationKey),
        }),
        {} as Record<CellParameters, string>
      ),
    [t]
  );

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className={cn(
          'h-8 w-[140px] bg-white',
          disabled && 'pointer-events-none animate-pulse opacity-60'
        )}
      >
        <SelectValue placeholder={t('devices.select.parameters.selectParameter')} />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(translatedLabels).map(([key, label]) => (
          <SelectItem key={key} value={key}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Legend({ parameter }: { parameter: CellParameters }) {
  const { t } = useTranslation();
  const label = useMemo(() => t(PARAMETER_LABELS[parameter]), [parameter, t]);

  return (
    <div className="h-min w-min justify-start p-1 px-2 select-none">
      <div className="flex items-center gap-2">
        <span
          className="size-4 rounded-full"
          style={{ backgroundColor: PARAMETER_COLORS[parameter] }}
        />
        <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
          {label}
        </span>
      </div>
    </div>
  );
}

function CellManagement({
  deviceId,
  moduleNumber,
}: {
  deviceId: string | undefined;
  moduleNumber: number;
}) {
  const isMobile = useMediaQuery('(max-width:640px)');
  const [selectedParameter, setSelectedParameter] = useState<CellParameters>('voltage');
  const { t } = useTranslation();

  const { data, isLoading, isFetching } = useGetBMSStatusDeviceQuery({
    deviceId,
    moduleNumber,
  });

  const barData = useMemo(
    () =>
      data?.cells?.map((d, i) => ({
        voltage: d.V,
        temperature: d.T,
        soh: d.SoH,
        Cell: i + 1,
      })) ?? [],
    [data?.cells]
  );

  const areAnyPointsAvailable = useMemo(
    () => Boolean(barData.length) && barData.some((d) => d[selectedParameter] != null),
    [barData, selectedParameter]
  );

  return (
    <div className="flex h-full flex-col justify-between gap-2">
      <div className="flex flex-col justify-between gap-2 sm:flex-row">
        <h3 className="font-semibold text-nowrap">
          {t('devices.batteryModule.cellsManagement')}
        </h3>
        <div className={cn(isFetching && 'pointer-events-none animate-pulse opacity-60')}>
          <ParameterSelect
            value={selectedParameter}
            onChange={setSelectedParameter}
            disabled={isFetching}
          />
        </div>
      </div>
      <div className="h-full">
        {isLoading && (
          <div className="flex h-full min-h-[238px] w-full items-center justify-center">
            <LoadingSpinner className="size-14" />
          </div>
        )}
        {!isLoading && !areAnyPointsAvailable && (
          <div className="flex h-full min-h-[238px] w-full items-center justify-center rounded-md bg-gray-50">
            <span className="text-muted-foreground">
              {t('devices.batteryModule.noDataFound')}
            </span>
          </div>
        )}
        {!isLoading && areAnyPointsAvailable && (
          <div className={cn(isFetching && 'pointer-events-none animate-pulse opacity-60')}>
            <CellChart
              data={barData}
              selectedParameter={selectedParameter}
              isMobile={isMobile}
              isFetching={isFetching}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function CellChart({
  data,
  selectedParameter,
  isMobile,
}: {
  data?: {
    voltage: number;
    temperature: number;
    soh: number;
    Cell: number;
  }[];
  selectedParameter: CellParameters;
  isMobile: boolean;
  isFetching: boolean;
}) {
  return (
    <>
      <ResponsiveContainer width="100%" height={210}>
        <BarChart
          data={data}
          margin={{
            top: 0,
            right: 0,
            left: -10,
            bottom: 0,
          }}
        >
          <XAxis dataKey="Cell" style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }} />
          <YAxis
            domain={selectedParameter === 'voltage' ? [0, 4.5] : [0, 'auto']}
            label={{
              value: PARAMETER_UNITS[selectedParameter],
              angle: -90,
              position: 'left',
              offset: isMobile ? -20 : -18,
            }}
            style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
          />
          <Tooltip cursor={false} content={(props) => <ChartTooltipComponent {...props} />} />
          <Bar
            key={selectedParameter}
            dataKey={selectedParameter}
            stroke={PARAMETER_COLORS[selectedParameter]}
            fill={PARAMETER_COLORS[selectedParameter]}
            radius={[10, 10, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>

      <div className="mx-auto flex w-full flex-wrap justify-center gap-1 md:gap-1">
        <Legend parameter={selectedParameter} />
      </div>
    </>
  );
}

const ChartTooltipComponent = ({
  payload,
  label,
}: TooltipContentProps<ValueType, NameType>) => {
  const { t } = useTranslation();
  const translatedLabels = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(PARAMETER_LABELS).map(([key, translationKey]) => [
          key,
          t(translationKey),
        ])
      ) as Record<CellParameters, string>,
    [t]
  );

  return (
    <div className="flex flex-col border border-gray-300 bg-white p-4 text-base">
      <p>
        {t('devices.batteryModule.cell')} {label}
      </p>
      <div className="mt-2">
        {payload?.map((p: { name?: string | number; payload?: Record<string, unknown> }) => (
          <ul key={p.name}>
            {Object.entries(PARAMETER_UNITS).map(([key, unit]) => (
              <li key={key} style={{ color: PARAMETER_COLORS[key as CellParameters] }}>
                {translatedLabels[key as CellParameters]}:{' '}
                {formatValueOrDefault(p.payload?.[key] as number | null | undefined, unit)}
              </li>
            ))}
          </ul>
        ))}
      </div>
    </div>
  );
};
